/**
 * 生年月日の部品組立て・年齢算出ユーティリティ（フロントエンド版）
 * バックエンド版: backend/adonis/app/Helper/index.js getAgeFromBirth
 * ロジック変更時は両方を同時に更新すること
 *
 * ただし「JST の壁時計へ固定する」処理はフロントエンド版にしか無い。サーバは Asia/Tokyo で動くので
 * 素の moment() がそのまま JST 壁時計になるが、ブラウザは利用者の端末タイムゾーンで解釈するため、
 * 生年月日・基準日の両方を明示的に JST へ戻さないと日付が 1 日ずれて年齢の境界を跨ぐ。
 *
 * 不変条件: 日付を文字列で組み立てて moment() に渡さない。
 *   非 ISO 形式（'2026-5-3' や空白入りの '2026 - 5 - 3' 等）のパースはブラウザの
 *   エンジン実装依存で Invalid になる（WebKit で実際に Invalid になり、年齢が算出できず
 *   保護者同意ゲートが確定不能になった事象がある）。組み立てるなら moment のオブジェクト形式で渡す。
 *
 * 不変条件: 誕生日の到来判定に「今年の誕生日」を日付として組み立てない。月日を直接比較する。
 *   2/29 生まれは非閏年に「今年の 2/29」が存在せず、日付として組み立てると invalid になって
 *   到来判定が常に false 側へ倒れる（＝非閏年の 1/1〜2/28 に 1 歳多く出る）。
 *   月日比較なら 2/28 までは未到来・3/1 以降は到来済みとなり、閏年でも非閏年でも一貫する。
 *
 * このユーティリティで防げるのは再実装ミス（書式ミス・0 埋め漏れの再発）であり、
 * 新しく年齢や生年月日を扱う経路が「ここを呼び忘れる」ことは防げない。
 * 年齢算出・日付組立てを足すときはレビュー観点に含めること。
 */
import moment from 'moment';

/** users.birthday は JST の壁時計値として保存されている。API は ISO-UTC で返すため固定オフセットで戻す */
const JST_OFFSET_MINUTES = 540;

/**
 * moment オブジェクトから年月日を取り出す。
 *
 * @param {object} m moment オブジェクト
 * @returns {{ year: number, month: number, day: number }}
 */
function partsOf(m) {
  return { year: m.year(), month: m.month() + 1, day: m.date() };
}

/**
 * 生年月日の値を「JST 壁時計としての年月日」へ分解する。
 *
 * users.birthday は DATETIME で、API は ISO-UTC（例 '1979-01-04T15:00:00.000Z' ＝ JST 1979-01-05）
 * で返す。素の moment() で解釈すると端末のタイムゾーンで壁時計が動き、非 JST 端末では
 * 生年月日が 1 日ずれて年齢の境界を跨ぐ。タイムゾーン情報を持つ値は JST 固定で壁時計へ戻す。
 * 一方、日付のみ・タイムゾーンなしの値はオフセットを当てると逆にずれるので、部品を直接読む。
 *
 * @param {string|Date|null} value
 * @returns {{ year: number, month: number, day: number }|null}
 */
function toDatePartsFromValue(value) {
  // 有効な生年月日は falsy になりえないので、falsy はまとめて「値なし」に倒す。
  // 列挙（null / undefined / ''）だと 0 が漏れ、moment('0') が 2000-01-01 に解決してしまう
  if (!value) return null;

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return partsOf(moment(value).utcOffset(JST_OFFSET_MINUTES));
  }

  const str = String(value).trim();

  // 日付のみ: そのまま壁時計として読む（オフセットを当てると前日へずれる）
  const dateOnly = str.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnly) {
    return { year: Number(dateOnly[1]), month: Number(dateOnly[2]), day: Number(dateOnly[3]) };
  }

  // タイムゾーンを持たない datetime: 記載どおりの壁時計として読む
  const naive = str.match(/^(\d{4})-(\d{2})-(\d{2})[T ]\d{2}:\d{2}(:\d{2})?(\.\d+)?$/);
  if (naive) {
    return { year: Number(naive[1]), month: Number(naive[2]), day: Number(naive[3]) };
  }

  // タイムゾーンを持つ値（ISO-UTC 等）: JST 固定で壁時計へ戻す
  const parsed = moment(str);
  if (!parsed.isValid()) return null;
  return partsOf(parsed.utcOffset(JST_OFFSET_MINUTES));
}

/**
 * 日付部品を 2 桁へ 0 埋めする（冪等）。
 * 桁数で判定するので、既に 0 埋め済みの選択済み option を選び直しても二重 0 埋めにならない。
 *
 * @param {string|number|null} value 年月日のいずれかの部品
 * @returns {string} '9' → '09' / '10' → '10' / '09' → '09'。null・undefined は ''
 */
export function padDatePart(value) {
  if (value === null || value === undefined) return '';
  const str = String(value);
  return str.length === 1 ? `0${str}` : str;
}

/**
 * 年月日から送信用の ISO 日付文字列を組み立てる。
 *
 * 暦としての妥当性（2 月 31 日など）は検査しない — 検査して null を返すと、
 * 送信値が黙って消えて「入力したのに保存されない」になる。組合せの妥当性は
 * 入力側の必須チェックと保存側で弾く。ここが担うのは書式（0 埋め・区切り）の一元化。
 *
 * @param {string|number|null} year
 * @param {string|number|null} month
 * @param {string|number|null} day
 * @returns {string|null} 'YYYY-MM-DD'。欠損・非数値・範囲外は null
 */
export function toISODateString(year, month, day) {
  const y = Number(year);
  const m = Number(month);
  const d = Number(day);
  // 未入力とプレースホルダ option（value=0）はいずれも数値 0 になる＝日付として成立しない
  if (!y || !m || !d) return null;
  if (!Number.isInteger(y) || !Number.isInteger(m) || !Number.isInteger(d)) return null;
  if (y < 1 || y > 9999 || m < 1 || m > 12 || d < 1 || d > 31) return null;
  return `${String(y).padStart(4, '0')}-${padDatePart(m)}-${padDatePart(d)}`;
}

/**
 * 年月日の部品から年齢を算出する。
 *
 * @param {{ year: string|number|null, month: string|number|null, day: string|number|null }} parts
 * @param {string|Date|null} base 年齢の基準日（省略時は現在時刻）
 * @returns {number|null} 年齢。0 は有効値。判定不能（欠損・不正な日付・負値）は null
 */
export function calcAgeFromParts(parts, base = null) {
  if (!parts) return null;
  const year = Number(parts.year);
  const month = Number(parts.month);
  const day = Number(parts.day);
  if (!year || !month || !day) return null;
  // Number ガードを通過した不正な組合せ（2 月 31 日など）を弾くための妥当性検査。
  // 文字列を組まず moment のオブジェクト形式へ渡す（冒頭の不変条件）
  if (!moment({ year, month: month - 1, date: day }).isValid()) return null;
  // 基準日も生年月日と同じ「JST 壁時計」へ揃えてから比較する。片方だけ JST 固定にすると、
  // 非 JST 端末では基準日だけが 1 日ずれて誕生日の到来判定が反転する（差が TZ 不変になるのは
  // 両辺が同じ表現のときだけ）。基準日を省略したときの「今」も JST の壁時計で採る。
  const baseParts = base
    ? toDatePartsFromValue(base)
    : partsOf(moment(new Date()).utcOffset(JST_OFFSET_MINUTES));
  if (!baseParts) return null;
  const yearDiff = baseParts.year - year;
  const hasHadBirthday = baseParts.month > month
    || (baseParts.month === month && baseParts.day >= day);
  const age = hasHadBirthday ? yearDiff : yearDiff - 1;
  // age === 0（生後 1 年未満）は有効値。負値＝生年月日が基準日より後 ＝判定不能とする
  return age < 0 ? null : age;
}

/**
 * 保存済みの生年月日（API レスポンス・Date）から年齢を算出する。
 *
 * @param {string|Date|null} birthValue 生年月日（ISO-UTC / 'YYYY-MM-DD' / naive datetime / Date）
 * @param {string|Date|null} base 年齢の基準日（省略時は現在時刻）
 * @returns {number|null} 年齢。0 は有効値。判定不能は null
 */
export function calcAgeFromBirthday(birthValue, base = null) {
  const parts = toDatePartsFromValue(birthValue);
  return parts ? calcAgeFromParts(parts, base) : null;
}
